import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { number, object } from 'yup';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { HeaderMainNav } from 'Src/components/navigation/HeaderMainNav';
import { TableRow } from 'Src/feature/data-table/TableRow';
import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { useToast } from 'Src/feature/toasts/useToast';
import { useTrainingMode } from 'Src/feature/training-mode/useTrainingMode';
import useRoute from 'Src/routes/useRoute';

import { useCashBank } from './useCashBank';

interface FormModel {
  amount: number;
  employeeId: number;
}

const sectionId = 'CashBankList';
const { Form, Field } = getTypedFormComponents<FormModel>();

const CashBankList: FC = () => {
  const { t } = useTranslation();
  const { crumbs, navigate } = useRoute();
  const toast = useToast();
  const trainingMode = useTrainingMode();

  const cashBanks = useCashBank.query.getAll();
  const cashBankAssignOne = useCashBank.mutate.assignOne();
  const employeesClockedIn = useEmployee.query.clockedInList();
  const employeesMap = useEmployee.query.map();

  const form = useForm<FormModel>({
    schema: object().shape({
      amount: number().label(t('cashBank.labels.inDollarAmount')).required(),
      employeeId: number().label(t('cashBank.labels.assignTo')).required(),
    }),
  });

  const modal = useModalSearchParamControl({
    value: 'open-cash-bank',
    onOpen: () => {
      form.action.reset({});
    }
  });

  const onSave = async () => {
    const { isValid, values } = await form.action.submit();
    if (!isValid) return;
    cashBankAssignOne.mutate(values, {
      onSuccess: () => {
        modal.close();
      },
      onError: (err) => {
        toast.show.serviceError(err, { id: `${sectionId}.onSave` });
      },
    });
  };

  const table = useDataTable({ data: cashBanks.data || [] });
  const { Table, Column } = table;
  const showPagination = table.store.data.all.length > 15;

  return (
    <Page
      sectionId={sectionId}
      main={(
        <PageMain
          header={(
            <PageMainHeader
              start={(
                <Flex horizontal gap="xs" alignItems="center">
                  <FontIcon name="local_atm" />
                  <Copy variant="h6" value={t('cashBank.label_plural')} />
                </Flex>
              )}
              end={<HeaderMainNav />}
            />
          )}
          main={(
            <Flex fullHeight>
              <Flex horizontal justifyContent="flex-end">
                <CTA
                  color="primary"
                  onClick={modal.open}
                  label={t('openBank.label')}
                  disabled={trainingMode.isTraining}
                />
              </Flex>
              <Table
                pagination={showPagination ? undefined : <></>}
                noDataMessage={t('cashBank.errors.getAll_noData')}
                renderer={{
                  row: (dataItem) => ({ children }) => (
                    <TableRow
                      onClick={() => {
                        navigate(crumbs.pos.cashBank.main.interpolate({
                          cashBankId: dataItem.id,
                        }));
                      }}
                    >
                      {children}
                    </TableRow>
                  )
                }}
              >
                <Column
                  label={t('cashBank.labels.assignedTo')}
                  field="employeeId"
                  cellContentRenderer={({ value }) => {
                    const employee = employeesMap.data?.get(value);
                    return (
                      <Copy optional value={employee ? t('labels.util.employeeFullName', employee) : undefined} />
                    );
                  }}
                />
                <Column label={t('labels.status')} field="status" />
                <Column
                  label={t('cashBank.labels.openedAt')}
                  field="createdAt"
                  cellContentRenderer={({ value }) => value ? DateTime.fromMillis(value).toLocaleString(DateTime.TIME_SIMPLE) : ''}
                />
              </Table>
              <Modal
                data-testid="cash-bank-new-modal"
                open={modal.isOpen}
                onBackDropClick={modal.close}
              >
                <ModalHeader closeButton={<CloseIconButton onClick={modal.close} />}>
                  <ModalTitle copy={t('cashBank.labels.startingAmount')} />
                </ModalHeader>

                <ModalContent>
                  <Form usedForm={form} omitChangeTracking>
                    <Field
                      type="currency"
                      name="amount"
                      label={t('cashBank.labels.inDollarAmount')}
                    />
                    <Field
                      type="select"
                      name="employeeId"
                      label={t('cashBank.labels.assignTo')}
                      variant="contained"
                      fullWidth
                      options={(
                        employeesClockedIn.isLoading
                          ? []
                          : employeesClockedIn.data || []
                      ).map(({ id, firstName, lastName }) => ({
                        value: id,
                        label: t('labels.util.employeeFullName', { firstName, lastName }),
                      }))}
                    />
                  </Form>
                </ModalContent>

                <ModalActions>
                  <ModalAction onClick={onSave} color="primary" label={t('cashBank.labels.openBank')} />
                </ModalActions>
              </Modal>
            </Flex>
          )}
        />
      )}
    />
  );
};

// FYI: we diverge from our named export pattern in cases where a file is using code splitting
// - React.lazy does not support named exports for code split files
export default CashBankList;
